import JSONApiService from "@/services/JSONApiService";
import { USER_MODEL, USER_STAFF_MODEL, INVITE_USER } from "@/models/user-model";
import { disablePluralizeForCall } from "@/services/utils";
import APIService from "@/services/APIService";
import {
  SETTINGS,
  ROLES,
  include,
  ORGANISATION,
  ORGANISATIONS,
  ADDRESSES,
  WORKING_HOURS_GROUPINGS,
  CONTRACT_TYPES_GROUPINGS
} from "@/models/relationships";
import { EXPERIENCE_FUNCTION_MODEL } from "@/models/experience-function-model";
import { LOCATION_GROUPING_MODEL } from "@/models/location-grouping-model";
import { JOB_PREFERENCE_MODEL } from "@/models/job-preference-model";
import { DIVERSITY_TRAIT_MODEL } from "@/models/diversity-trait-model";
import { EXPERIENCE_MODEL } from "@/models/experience-model";
import { LANGUAGE_MODEL } from "@/models/language-model";
import { SKILL_MODEL } from "@/models/skill-model";
import { QUALIFICATION_MODEL } from "@/models/qualification-model";
import pluralize from "pluralize";

const userIncludes = include(
  ADDRESSES,
  ORGANISATION,
  ORGANISATIONS,
  pluralize(EXPERIENCE_FUNCTION_MODEL),
  pluralize(LOCATION_GROUPING_MODEL),
  pluralize(WORKING_HOURS_GROUPINGS),
  pluralize(CONTRACT_TYPES_GROUPINGS),
  pluralize(EXPERIENCE_MODEL),
  pluralize(LANGUAGE_MODEL),
  pluralize(SKILL_MODEL),
  pluralize(QUALIFICATION_MODEL)
);

const userApi = commit => ({
  getBookingManagers: async organisationId =>
    JSONApiService(commit)
      .all(USER_MODEL)
      .get({ filter: { booking_manager_org_id: organisationId } }),
  getUsers: async params =>
    JSONApiService(commit)
      .all(USER_MODEL)
      .get(params),
  getUserRoles: async userId =>
    JSONApiService(commit)
      .one(USER_MODEL, userId)
      .all(ROLES)
      .get(),
  getUserSettings: async () => {
    const api = JSONApiService(commit);
    disablePluralizeForCall(api, () => {
      api.all(USER_MODEL);
    });
    return api.all(SETTINGS).get();
  },
  editUserSettings: async editedSettings =>
    APIService.client().patch(`${USER_MODEL}/${SETTINGS}`, {
      data: editedSettings
    }),
  createUser: async user =>
    JSONApiService(commit).create(USER_STAFF_MODEL, user),
  editUserStaffDetails: async user =>
    JSONApiService(commit).update(USER_STAFF_MODEL, user),
  editUser: async (id, data) =>
    JSONApiService(commit)
      .one(USER_MODEL, id)
      .patch(data, userIncludes),
  getUser: async id =>
    JSONApiService(commit)
      .one(USER_MODEL, id)
      .get(userIncludes),
  getUserExperienceFunctions: async userId =>
    JSONApiService(commit)
      .one(USER_MODEL, userId)
      .all(EXPERIENCE_FUNCTION_MODEL)
      .get(),
  getUserLocationGroupings: async userId =>
    JSONApiService(commit)
      .one(USER_MODEL, userId)
      .all(LOCATION_GROUPING_MODEL)
      .get(),
  addUserPreference: async (userId, data) =>
    JSONApiService(commit)
      .one(USER_MODEL, userId)
      .all(JOB_PREFERENCE_MODEL)
      .patch(data),
  addUserDiversityTraits: async (userId, data) =>
    JSONApiService(commit)
      .one(USER_MODEL, userId)
      .all(DIVERSITY_TRAIT_MODEL)
      .post(data),
  getUserDiversityTraits: async userId =>
    JSONApiService(commit)
      .one(USER_MODEL, userId)
      .all(DIVERSITY_TRAIT_MODEL)
      .get(),
  inviteUser: async id => {
    const api = JSONApiService(commit);
    api.one(USER_MODEL, id);
    disablePluralizeForCall(api, () => {
      api.all(INVITE_USER);
    });
    return api.post({});
  },
  createWorkerAccount: async () =>
    APIService.client().post(`${USER_MODEL}/create-worker-account`, {})
});

export default userApi;
